import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mountain, LogOut, Calendar, ShoppingBag, FileText, Package, LayoutDashboard, Plus, Pencil, Trash2, Loader2, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getStats, getBookings, getPurchases, getBlog, createPost, updatePost, deletePost, getToolkits, updateToolkit, previewToolkitPdf, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const inputCls = "bg-forest-950 border-forest-700 text-sage-100 focus-visible:ring-golden-500 rounded-sm";
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-forest-900 border border-forest-700 rounded-sm p-6">
    <Icon className="h-6 w-6 text-golden-500 mb-4" />
    <div className="font-serif text-4xl text-sage-100">{value}</div>
    <div className="text-sage-400 text-sm mt-1">{label}</div>
  </div>
);

const Dashboard = () => {
  const { admin, signOut } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [posts, setPosts] = useState([]);
  const [toolkits, setToolkits] = useState([]);

  const [postDialog, setPostDialog] = useState(null); // {mode, data}
  const [toolkitDialog, setToolkitDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    getStats().then(setStats).catch(() => {});
    getBookings().then(setBookings).catch(() => {});
    getPurchases().then(setPurchases).catch(() => {});
    getBlog().then(setPosts).catch(() => {});
    getToolkits().then(setToolkits).catch(() => {});
  };
  useEffect(refresh, []);

  const logout = () => { signOut(); window.location.href = "/admin/login"; };

  // ---- Blog handlers ----
  const emptyPost = { slug: "", title: "", excerpt: "", category: "", cover: "/generated/resources.png", read_time: "5 min read", body: "" };
  const savePost = async () => {
    const d = postDialog.data;
    if (!d.slug || !d.title || !d.body) { toast.error("Slug, title and body are required."); return; }
    setSaving(true);
    try {
      if (postDialog.mode === "new") await createPost(d);
      else await updatePost(postDialog.originalSlug, d);
      toast.success("Post saved.");
      setPostDialog(null);
      getBlog().then(setPosts);
      getStats().then(setStats);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const removePost = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try { await deletePost(slug); toast.success("Deleted."); getBlog().then(setPosts); getStats().then(setStats); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Toolkit handlers ----
  const saveToolkit = async () => {
    const d = toolkitDialog.data;
    setSaving(true);
    try {
      await updateToolkit(toolkitDialog.id, {
        name: d.name, tagline: d.tagline, description: d.description,
        starting_at: d.starting_at, includes: d.includes, image: d.image, featured: !!d.featured,
      });
      toast.success("Toolkit updated.");
      setToolkitDialog(null);
      getToolkits().then(setToolkits);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-forest-950">
      <header className="border-b border-forest-700 bg-forest-950/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="h-6 w-6 text-golden-500" />
            <span className="font-serif text-2xl text-sage-100">Cascade<span className="text-golden-500">.</span> Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sage-400 text-sm hidden sm:inline">{admin?.email}</span>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-sage-200 hover:text-golden-500 transition-colors duration-300 text-sm">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="bg-forest-900 border border-forest-700 mb-8 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-golden-500 data-[state=active]:text-navy text-sage-200"><LayoutDashboard className="h-4 w-4 mr-2" />Overview</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-golden-500 data-[state=active]:text-navy text-sage-200"><Calendar className="h-4 w-4 mr-2" />Bookings</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases" className="data-[state=active]:bg-golden-500 data-[state=active]:text-navy text-sage-200"><ShoppingBag className="h-4 w-4 mr-2" />Purchases</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="data-[state=active]:bg-golden-500 data-[state=active]:text-navy text-sage-200"><FileText className="h-4 w-4 mr-2" />Resources</TabsTrigger>
            <TabsTrigger value="toolkits" data-testid="tab-toolkits" className="data-[state=active]:bg-golden-500 data-[state=active]:text-navy text-sage-200"><Package className="h-4 w-4 mr-2" />Toolkits</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard icon={Calendar} label="Call requests" value={stats?.bookings ?? "—"} />
              <StatCard icon={ShoppingBag} label="Paid orders" value={stats?.paid_orders ?? "—"} />
              <StatCard icon={ShoppingBag} label="Revenue" value={stats ? `$${stats.revenue.toFixed(0)}` : "—"} />
              <StatCard icon={FileText} label="Articles" value={stats?.posts ?? "—"} />
              <StatCard icon={Package} label="Toolkits" value={stats?.toolkits ?? "—"} />
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings">
            <div className="space-y-3">
              {bookings.length === 0 && (
                <div className="bg-forest-900 border border-forest-700 rounded-sm py-10 text-center text-sage-400">No call requests yet.</div>
              )}
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-forest-900 border border-forest-700 rounded-sm p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <div>
                      <div className="font-serif text-xl text-sage-100">{b.name}</div>
                      <a href={`mailto:${b.email}`} className="text-golden-500 text-sm break-all">{b.email}</a>
                    </div>
                    <span className="text-sage-400 text-xs">{fmtDate(b.created_at)}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-sage-400">Organization: </span><span className="text-sage-200">{b.organization || "—"}</span></div>
                    <div><span className="text-sage-400">Segment: </span><span className="text-sage-200">{b.segment || "—"}</span></div>
                    <div><span className="text-sage-400">Interested in: </span><span className="text-sage-200">{b.service_interest || "—"}</span></div>
                    <div><span className="text-sage-400">Preferred time: </span><span className="text-sage-200">{b.preferred_time || "—"}</span></div>
                  </div>
                  {b.message && (
                    <div className="mt-3 pt-3 border-t border-forest-700">
                      <span className="text-sage-400 text-xs uppercase tracking-wider">Message</span>
                      <p className="text-sage-200 text-sm mt-1 whitespace-pre-wrap">{b.message}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PURCHASES */}
          <TabsContent value="purchases">
            <div className="space-y-3">
              {purchases.length === 0 && (
                <div className="bg-forest-900 border border-forest-700 rounded-sm py-10 text-center text-sage-400">No purchases yet.</div>
              )}
              {purchases.map((p) => (
                <div key={p.session_id} className="bg-forest-900 border border-forest-700 rounded-sm p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="font-serif text-lg text-sage-100">{p.toolkit_name}</div>
                    <span className={`text-xs px-2 py-1 rounded-sm shrink-0 ${p.payment_status === "paid" ? "bg-golden-500 text-navy" : "bg-forest-800 text-sage-300"}`}>{p.payment_status}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div><span className="text-sage-400">Customer: </span><span className="text-sage-200 break-all">{p.email || "—"}</span></div>
                    <div><span className="text-sage-400">Amount: </span><span className="text-sage-200">${((p.amount || 0) / 100).toFixed(2)}</span></div>
                    <div className="sm:col-span-2"><span className="text-sage-400">When: </span><span className="text-sage-200">{fmtDate(p.created_at)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog">
            <div className="flex justify-end mb-4">
              <button data-testid="new-post-btn" onClick={() => setPostDialog({ mode: "new", data: { ...emptyPost } })} className="flex items-center gap-2 bg-golden-500 text-navy font-semibold px-4 py-2.5 rounded-sm hover:bg-golden-600 transition-colors duration-300 text-sm">
                <Plus className="h-4 w-4" /> New article
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {posts.map((p) => (
                <div key={p.slug} className="bg-forest-900 border border-forest-700 rounded-sm p-5 flex justify-between gap-4">
                  <div>
                    <span className="text-xs uppercase tracking-wider text-golden-500 font-semibold">{p.category}</span>
                    <h3 className="font-serif text-xl text-sage-100 mt-1">{p.title}</h3>
                    <p className="text-sage-400 text-sm mt-2 line-clamp-2">{p.excerpt}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button data-testid={`edit-post-${p.slug}`} onClick={() => setPostDialog({ mode: "edit", originalSlug: p.slug, data: { ...emptyPost, ...p } })} className="text-sage-300 hover:text-golden-500 transition-colors duration-300"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-post-${p.slug}`} onClick={() => removePost(p.slug)} className="text-sage-300 hover:text-red-400 transition-colors duration-300"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TOOLKITS */}
          <TabsContent value="toolkits">
            <div className="grid md:grid-cols-2 gap-4">
              {toolkits.map((t) => (
                <div key={t.id} className="bg-forest-900 border border-forest-700 rounded-sm p-5 flex justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl text-sage-100">{t.name}</h3>
                    <p className="text-sage-400 text-sm mt-1">{t.tagline}</p>
                    <p className="text-golden-500 text-sm mt-2">{t.starting_at}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      data-testid={`preview-pdf-${t.id}`}
                      title="Download the current PDF for review"
                      onClick={async () => {
                        try {
                          await previewToolkitPdf(t.lookup_key, `${t.name.replace(/\s+/g, "_")}_preview.pdf`);
                        } catch (e) {
                          toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't download preview");
                        }
                      }}
                      className="flex items-center gap-2 bg-golden-500 text-navy text-xs font-semibold px-3 py-2 rounded-sm hover:bg-golden-600 transition-colors duration-300"
                    >
                      <Download className="h-3.5 w-3.5" /> Preview PDF
                    </button>
                    <button data-testid={`edit-toolkit-${t.id}`} onClick={() => setToolkitDialog({ id: t.id, data: { ...t, includes: [...(t.includes || [])] } })} className="text-sage-300 hover:text-golden-500 transition-colors duration-300 shrink-0 self-end"><Pencil className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* POST DIALOG */}
      <Dialog open={!!postDialog} onOpenChange={(o) => !o && setPostDialog(null)}>
        <DialogContent className="bg-forest-900 border-forest-700 text-sage-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{postDialog?.mode === "new" ? "New article" : "Edit article"}</DialogTitle><DialogDescription className="text-sage-400">Write or edit a resource article. It publishes to /resources instantly.</DialogDescription></DialogHeader>
          {postDialog && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sage-300 text-sm">Title</Label><Input data-testid="post-title" value={postDialog.data.title} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, title: e.target.value } })} className={inputCls} /></div>
                <div><Label className="text-sage-300 text-sm">Slug</Label><Input data-testid="post-slug" value={postDialog.data.slug} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, slug: e.target.value } })} className={inputCls} /></div>
                <div><Label className="text-sage-300 text-sm">Category</Label><Input value={postDialog.data.category} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, category: e.target.value } })} className={inputCls} /></div>
                <div><Label className="text-sage-300 text-sm">Read time</Label><Input value={postDialog.data.read_time} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, read_time: e.target.value } })} className={inputCls} /></div>
              </div>
              <div><Label className="text-sage-300 text-sm">Cover image path</Label><Input value={postDialog.data.cover} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, cover: e.target.value } })} className={inputCls} /></div>
              <div><Label className="text-sage-300 text-sm">Excerpt</Label><Textarea value={postDialog.data.excerpt} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, excerpt: e.target.value } })} className={`${inputCls} h-20`} /></div>
              <div><Label className="text-sage-300 text-sm">Body (blank line between paragraphs)</Label><Textarea data-testid="post-body" value={postDialog.data.body} onChange={(e) => setPostDialog({ ...postDialog, data: { ...postDialog.data, body: e.target.value } })} className={`${inputCls} h-40`} /></div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-post-btn" onClick={savePost} disabled={saving} className="flex items-center gap-2 bg-golden-500 text-navy font-semibold px-5 py-2.5 rounded-sm hover:bg-golden-600 transition-colors duration-300 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save article"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOOLKIT DIALOG */}
      <Dialog open={!!toolkitDialog} onOpenChange={(o) => !o && setToolkitDialog(null)}>
        <DialogContent className="bg-forest-900 border-forest-700 text-sage-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit toolkit</DialogTitle><DialogDescription className="text-sage-400">Edit display content. Price and checkout are managed via Stripe.</DialogDescription></DialogHeader>
          {toolkitDialog && (
            <div className="space-y-4">
              <div><Label className="text-sage-300 text-sm">Name</Label><Input data-testid="toolkit-name" value={toolkitDialog.data.name} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, name: e.target.value } })} className={inputCls} /></div>
              <div><Label className="text-sage-300 text-sm">Tagline</Label><Input value={toolkitDialog.data.tagline} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, tagline: e.target.value } })} className={inputCls} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sage-300 text-sm">{'"Starting at" label'}</Label><Input value={toolkitDialog.data.starting_at} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, starting_at: e.target.value } })} className={inputCls} /></div>
                <div><Label className="text-sage-300 text-sm">Image path</Label><Input value={toolkitDialog.data.image} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, image: e.target.value } })} className={inputCls} /></div>
              </div>
              <div><Label className="text-sage-300 text-sm">Description</Label><Textarea value={toolkitDialog.data.description} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, description: e.target.value } })} className={`${inputCls} h-24`} /></div>
              <div><Label className="text-sage-300 text-sm">Includes (one per line)</Label><Textarea data-testid="toolkit-includes" value={(toolkitDialog.data.includes || []).join("\n")} onChange={(e) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, includes: e.target.value.split("\n").filter(Boolean) } })} className={`${inputCls} h-28`} /></div>
              <div className="flex items-center gap-3"><Switch checked={!!toolkitDialog.data.featured} onCheckedChange={(v) => setToolkitDialog({ ...toolkitDialog, data: { ...toolkitDialog.data, featured: v } })} /><Label className="text-sage-300 text-sm">Featured (show on homepage teaser)</Label></div>
              <p className="text-xs text-sage-400">Note: price and checkout are managed via Stripe and edited separately.</p>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-toolkit-btn" onClick={saveToolkit} disabled={saving} className="flex items-center gap-2 bg-golden-500 text-navy font-semibold px-5 py-2.5 rounded-sm hover:bg-golden-600 transition-colors duration-300 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save toolkit"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
